import { pageAtTime, type PageSegment } from "../shared/raz-sync";
import type { PdfPageChangeSource } from "./pdf-preview";

/** 音频位置是唯一时钟，区分用户翻页与渲染完成，防止双向回调互相 seek。 */
export function bindPageAudioSync(
  audio: HTMLAudioElement,
  segments: PageSegment[],
  goToPage: (page: number) => void,
  onFollowingChange: (following: boolean, reason?: string) => void,
) {
  let disposed = false;
  let following = true;
  let ready = false;
  let currentPage = 1;
  let requestedPage: number | null = null;
  function followAudio() {
    if (disposed || !following || !ready) return;
    const page = pageAtTime(segments, audio.currentTime);
    if (page !== null && page !== currentPage && page !== requestedPage) {
      requestedPage = page;
      goToPage(page);
    }
  }
  function setFollowing(value: boolean, reason?: string) {
    if (disposed) return;
    following = value;
    requestedPage = null;
    onFollowingChange(value, reason);
    if (value) followAudio();
  }
  const events = ["timeupdate", "seeked", "play"] as const;
  for (const event of events) audio.addEventListener(event, followAudio);
  return {
    setFollowing,
    onPageChange(page: number, source: PdfPageChangeSource) {
      if (disposed) return;
      ready = true;
      currentPage = page;
      requestedPage = null;
      if (source === "user" && following) {
        const occurrences = segments.filter(segment => segment.page === page);
        const segment = occurrences.find(s => audio.currentTime >= s.start && audio.currentTime < s.end) ?? occurrences[0];
        if (!segment) {
          // 扉页、版权页等没有朗读时允许停留，不被 timeupdate 拉回。
          audio.pause();
          setFollowing(false, "此页没有朗读，已暂停；勾选后恢复跟随。");
          return;
        }
        audio.currentTime = segment.start;
      } else if (source === "initial" || source === "programmatic") {
        followAudio();
      }
    },
    dispose() {
      disposed = true;
      for (const event of events) audio.removeEventListener(event, followAudio);
    },
  };
}
